/**
 * ComparePage — /compare
 *
 * Side-by-side dossier for Proposal 01 (Baytown) vs Proposal 02 (North Houston District).
 * Designed for federal grant reviewers, Council aides, and District Board members
 * who need to understand at a glance how the same model scales across two
 * different Harris County communities — and why funding both together makes
 * structural sense (regional Hub consolidation, anchor employer diversification,
 * portfolio-mix complementarity).
 */
import { Link } from "react-router-dom";
import NDAGate from "@/components/site/NDAGate";
import SiteFooter from "@/components/site/SiteFooter";
import {
  systemTotals as bSystem,
  fiveYearForecast as bForecast,
  fiveYearGrandTotal as bGrand,
  fiveYearAllInTotal as bAllIn,
  programGoalsTotals as bGoals,
} from "@/lib/proposalData";
import {
  nhSystemTotals,
  nhFiveYearForecast,
  nhFiveYearGrandTotal,
  nhFiveYearAllInTotal,
  nhProgramGoalsTotals,
} from "@/lib/northHoustonData";
import { ArrowRight, MapPin, Building2, Briefcase, GraduationCap, TrendingUp, GitMerge, Landmark, ScrollText } from "lucide-react";

const fmt = (n) => `$${n.toLocaleString()}`;
const NAVY = "#0A192F";
const GOLD = "#D4AF37";
const RED = "#8B0000";
const CREAM = "#F9F7F1";

// ============ Compact side-by-side row ============
function StatRow({ label, a, b, accent }) {
  return (
    <div data-testid={`compare-row-${label.replace(/\W+/g, "-").toLowerCase()}`} className="grid grid-cols-12 gap-3 border-b border-[#CBD5E1] py-4 items-baseline">
      <div className="col-span-12 sm:col-span-4 font-display text-base text-[#0A192F]">{label}</div>
      <div className="col-span-6 sm:col-span-4 text-right sm:text-left">
        <div className="font-display text-xl" style={{ color: accent || NAVY }}>{a}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-0.5">Baytown</div>
      </div>
      <div className="col-span-6 sm:col-span-4 text-right sm:text-left">
        <div className="font-display text-xl" style={{ color: accent || NAVY }}>{b}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-0.5">North Houston</div>
      </div>
    </div>
  );
}

// ============ Anchor employer chips ============
function ChipList({ items, color }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <span
          key={it}
          className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border"
          style={{ color, borderColor: color }}
        >
          {it}
        </span>
      ))}
    </div>
  );
}

export default function ComparePage() {
  const baytownEmployers = ["ExxonMobil Baytown Complex", "Chevron Phillips", "Covestro", "INEOS Olefins", "DCP Midstream", "ExxonMobil Olefins Plant", "Bayer Material", "Lee College"];
  const nhEmployers = ["Amazon HOU2 / IXD9", "FedEx Express ZHOU", "Sysco Houston", "Coca-Cola Southwest", "Panelmatic", "Houston Methodist Willowbrook", "Lone Star North Harris", "HCC North"];

  // Combined regional view
  const combinedHH = bSystem.householdsServed + nhSystemTotals.householdsServed;
  const combinedYr1 = bSystem.grandTotal + nhSystemTotals.grandTotal;
  const combined5YrRecurring = bGrand + nhFiveYearGrandTotal;
  const combined5YrAllIn = bAllIn + nhFiveYearAllInTotal;
  const regionalHubConsolidated = 810000; // From NH Regional Hub scenarios for 2 deployments
  const baselineHubCombined = bSystem.centralHub + nhSystemTotals.centralHub;
  const annualHubSavings = baselineHubCombined - regionalHubConsolidated;

  return (
    <NDAGate proposal="compare">
    <main data-testid="compare-page" className="bg-[#F9F7F1] text-[#0A192F] min-h-screen">

      {/* ============ NAV ============ */}
      <nav className="sticky top-0 z-50 bg-[#0A192F] text-[#F9F7F1] border-b border-[#D4AF37]/30 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="compare-nav-home" className="flex items-center gap-3">
            <span className="font-display text-lg">The <em className="italic">Bridge</em> Model</span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">· Comparison Dossier</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/baytown" data-testid="compare-nav-baytown" className="hidden sm:inline-flex items-center gap-2 border border-[#F9F7F1]/30 hover:bg-[#F9F7F1]/10 px-3 h-9 font-mono text-[10px] uppercase tracking-widest">
              Proposal 01
            </Link>
            <Link to="/north-houston" data-testid="compare-nav-nh" className="hidden sm:inline-flex items-center gap-2 border border-[#F9F7F1]/30 hover:bg-[#F9F7F1]/10 px-3 h-9 font-mono text-[10px] uppercase tracking-widest">
              Proposal 02
            </Link>
            <Link to="/" data-testid="compare-nav-back" className="inline-flex items-center gap-2 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] px-4 h-9 font-mono text-[10px] uppercase tracking-widest">
              Back <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section className="relative bg-[#0A192F] text-[#F9F7F1] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div data-testid="compare-status-pill" className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/60 px-3 py-1.5 mb-6">
            <span className="inline-block h-1.5 w-1.5 bg-[#D4AF37] animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
              Both proposals · Pending community approval · Not yet adopted
            </span>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              Side-by-Side Dossier · Proposal 01 vs Proposal 02
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            One model.<br />
            <em className="italic text-[#D4AF37]">Two Harris County communities.</em><br />
            One regional federal pitch.
          </h1>
          <p className="mt-6 text-[#CBD5E1] text-sm sm:text-base max-w-3xl leading-relaxed">
            Bridge is the same operational playbook in every community — Hub-and-Spokes, three competitively selected agencies, LCSW-led clinical supervision, a 1:38 caseload cap. What changes is the local context: Baytown&apos;s petrochemical anchor base on the Channel, North Houston&apos;s IAH-corridor logistics &amp; aviation surge through Greenspoint. This page shows both at once so reviewers see the model — and the regional scale-up story — in a single view.
          </p>
        </div>
      </section>

      {/* ============ HEADLINE STATS RIBBON ============ */}
      <section className="bg-white py-16 lg:py-20 border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">Section 1 · Headline Investment</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight text-[#0A192F]">
                The <em className="italic">numbers</em>, at-a-glance.
              </h2>
            </div>
            <div className="lg:col-span-7 text-sm text-[#334155] leading-relaxed lg:pt-3">
              <p>
                Same operational caseload cap (1:38). Same three-portfolio architecture. Different community scale: Baytown&apos;s industrial base supports 1,500 households across 3 Spokes at ~$2,756/HH/year; North Houston District lands at 1,200 households across 3 Spokes at ~$2,880/HH/year. Combined Year-1 regional investment: <strong className="text-[#0A192F]">{fmt(combinedYr1)}</strong> · {combinedHH.toLocaleString()} households served annually.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Baytown Card */}
            <div data-testid="compare-card-baytown" className="bg-[#F9F7F1] border-l-4 border-[#0A192F] p-6 lg:p-7">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Proposal 01</span>
              </div>
              <div className="font-display text-2xl text-[#0A192F] mb-1">Baytown, Texas</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Harris County · Houston Ship Channel</div>
              <dl className="mt-5 grid grid-cols-2 gap-y-4">
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Year-1 Total</dt><dd className="font-display text-xl text-[#0A192F]">{fmt(bSystem.grandTotal)}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Households / Yr</dt><dd className="font-display text-xl text-[#0A192F]">{bSystem.householdsServed.toLocaleString()}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Cost / HH</dt><dd className="font-display text-xl text-[#8B0000]">{fmt(bSystem.costPerHousehold)}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Spokes × HH</dt><dd className="font-display text-xl text-[#0A192F]">3 × 500</dd></div>
                <div className="col-span-2"><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Recurring · All-In</dt><dd className="font-display text-lg text-[#0A192F]">{fmt(bGrand)} · {fmt(bAllIn)}</dd></div>
              </dl>
            </div>
            {/* NH Card */}
            <div data-testid="compare-card-nh" className="bg-[#F9F7F1] border-l-4 border-[#D4AF37] p-6 lg:p-7">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Proposal 02</span>
              </div>
              <div className="font-display text-2xl text-[#0A192F] mb-1">North Houston District</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">Harris County · IAH Corridor · Greenspoint</div>
              <dl className="mt-5 grid grid-cols-2 gap-y-4">
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Year-1 Total</dt><dd className="font-display text-xl text-[#0A192F]">{fmt(nhSystemTotals.grandTotal)}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Households / Yr</dt><dd className="font-display text-xl text-[#0A192F]">{nhSystemTotals.householdsServed.toLocaleString()}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Cost / HH</dt><dd className="font-display text-xl text-[#8B0000]">{fmt(nhSystemTotals.costPerHousehold)}</dd></div>
                <div><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">Spokes × HH</dt><dd className="font-display text-xl text-[#0A192F]">3 × 400</dd></div>
                <div className="col-span-2"><dt className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Recurring · All-In</dt><dd className="font-display text-lg text-[#0A192F]">{fmt(nhFiveYearGrandTotal)} · {fmt(nhFiveYearAllInTotal)}</dd></div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY CONTEXT ============ */}
      <section className="bg-[#F9F7F1] py-20 lg:py-24 border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">Section 2 · Local Context</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight text-[#0A192F]">
                Why <em className="italic">these two communities</em>, first.
              </h2>
            </div>
            <div className="lg:col-span-7 text-sm text-[#334155] leading-relaxed lg:pt-3">
              <p>Both proposals sit in Harris County. Both share the same county-level CDBG entitlement, the same Workforce Solutions Greater Houston region, and the same County Commissioners Court. What differs is the <em>anchor economic engine</em> each community uses to convert intensive case management into stable family income — and that&apos;s the lever that determines outcomes. Bridge meets each community at <em>its</em> economic edge.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            <div className="bg-white p-7 lg:p-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A192F] mb-2">Baytown · Ship Channel</div>
              <p className="text-[#334155] text-sm leading-relaxed mb-4">
                Anchored by the world&apos;s largest integrated petrochemical complex. Lee College feeds skilled-trade pipelines directly into ExxonMobil, Chevron Phillips, Covestro, INEOS. Households can move from minimum wage to $60k+ in 18 months on a single certified credential.
              </p>
              <ChipList items={baytownEmployers} color={NAVY} />
            </div>
            <div className="bg-white p-7 lg:p-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A192F] mb-2">North Houston District · IAH Corridor</div>
              <p className="text-[#334155] text-sm leading-relaxed mb-4">
                The fastest-growing logistics &amp; aviation corridor in Texas, anchored by IAH Airport. Lone Star North Harris and HCC North feed allied-health, CDL, and aviation-maintenance pipelines into Amazon, FedEx, Sysco, and Houston Methodist Willowbrook within 4 miles of every district household.
              </p>
              <ChipList items={nhEmployers} color={GOLD} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ WORKFORCE TRACK ============ */}
      <section className="bg-white py-20 lg:py-24 border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">Section 3 · Workforce Pipeline</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight text-[#0A192F]">
                <em className="italic">Same playbook</em>, different campus.
              </h2>
            </div>
            <div className="lg:col-span-7 text-sm text-[#334155] leading-relaxed lg:pt-3">
              <p>Every Bridge community partners with a community college on a connector-supervised cohort track. Cert programs are picked based on what the dominant employer actually hires. The Service Connector follows the household from enrollment through credential to placement and 12-month retention.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            <div className="bg-[#F9F7F1] p-7 lg:p-8">
              <GraduationCap className="h-7 w-7 text-[#0A192F] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A192F] mb-1">Baytown Track</div>
              <div className="font-display text-2xl text-[#0A192F] mb-3">Lee College</div>
              <ul className="text-sm text-[#334155] space-y-1.5 list-disc pl-5">
                <li>Process Technology · feeds ExxonMobil, Chevron Phillips, Covestro</li>
                <li>Industrial Maintenance · skilled trades pipeline</li>
                <li>Allied Health Certificates · CNA, MA, Pharm Tech</li>
                <li>CDL · transportation industry pipeline</li>
              </ul>
              <div className="mt-5 pt-4 border-t border-[#CBD5E1] grid grid-cols-2 gap-3">
                <div><div className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Enrolled</div><div className="font-display text-lg text-[#0A192F]">{bGoals.leeCollegeEnrollmentTotal.toLocaleString()}</div></div>
                <div><div className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Graduates</div><div className="font-display text-lg text-[#0A192F]">{bGoals.leeCollegeGraduatesTotal.toLocaleString()}</div></div>
              </div>
            </div>
            <div className="bg-[#F9F7F1] p-7 lg:p-8">
              <GraduationCap className="h-7 w-7 text-[#D4AF37] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A192F] mb-1">North Houston Track</div>
              <div className="font-display text-2xl text-[#0A192F] mb-3">Lone Star North Harris · HCC North</div>
              <ul className="text-sm text-[#334155] space-y-1.5 list-disc pl-5">
                <li>Allied Health · feeds Houston Methodist Willowbrook</li>
                <li>Logistics &amp; Supply Chain · feeds Amazon, FedEx, Sysco</li>
                <li>Aviation Maintenance &amp; Ground Ops · feeds IAH</li>
                <li>CDL Class A &amp; B · feeds Sysco, FedEx Freight</li>
              </ul>
              <div className="mt-5 pt-4 border-t border-[#CBD5E1] grid grid-cols-2 gap-3">
                <div><div className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Enrolled</div><div className="font-display text-lg text-[#0A192F]">{nhProgramGoalsTotals.lonestarEnrollmentTotal.toLocaleString()}</div></div>
                <div><div className="font-mono text-[9px] uppercase tracking-widest text-[#64748B]">5-Year Graduates</div><div className="font-display text-lg text-[#0A192F]">{nhProgramGoalsTotals.lonestarGraduatesTotal.toLocaleString()}</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BUDGET COMPARISON ============ */}
      <section className="bg-[#0A192F] text-[#F9F7F1] py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-15 pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-8">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Section 4 · Budget Structure</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight">
                <em className="italic text-[#D4AF37]">Cost per household</em> tells the real story.
              </h2>
            </div>
            <div className="lg:col-span-7 text-sm text-[#CBD5E1] leading-relaxed lg:pt-3">
              <p>Both proposals price the same model. The marginal difference in per-HH cost is driven by economies of scale (Baytown&apos;s 500-HH spokes vs NH&apos;s 400-HH spokes spread overhead across more cases). At the regional level, this gap closes to zero — both communities benefit from a shared Hub.</p>
            </div>
          </div>
          <div className="bg-[#F9F7F1] text-[#0A192F]">
            <div className="px-6 lg:px-8 pt-5">
              <StatRow label="Central Hub · Year 1" a={fmt(bSystem.centralHub)} b={fmt(nhSystemTotals.centralHub)} />
              <StatRow label="Per-Agency · Year 1" a={fmt(bSystem.perAgency)} b={fmt(nhSystemTotals.perAgency)} />
              <StatRow label="Total 3 Agencies · Year 1" a={fmt(bSystem.totalAgencies)} b={fmt(nhSystemTotals.totalAgencies)} />
              <StatRow label="Total Year-1 (Recurring)" a={fmt(bSystem.grandTotal)} b={fmt(nhSystemTotals.grandTotal)} accent={RED} />
              <StatRow label="Cost per Household / Year" a={fmt(bSystem.costPerHousehold)} b={fmt(nhSystemTotals.costPerHousehold)} accent={RED} />
              <StatRow label="Service-Connector Caseload" a={`1 : ${bSystem.caseload}`} b={`1 : ${nhSystemTotals.caseload}`} />
              <StatRow label="5-Year Recurring Total" a={fmt(bGrand)} b={fmt(nhFiveYearGrandTotal)} />
              <StatRow label="5-Year All-In (incl Dev Group)" a={fmt(bAllIn)} b={fmt(nhFiveYearAllInTotal)} />
            </div>
            <div className="px-6 lg:px-8 py-5 bg-[#0A192F] text-[#F9F7F1] grid sm:grid-cols-3 gap-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Combined Year-1</div>
                <div className="font-display text-2xl">{fmt(combinedYr1)}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{combinedHH.toLocaleString()} HH served</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Combined 5-Yr Recurring</div>
                <div className="font-display text-2xl">{fmt(combined5YrRecurring)}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{(combinedHH * 5).toLocaleString()} household-yrs</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Combined 5-Yr All-In</div>
                <div className="font-display text-2xl">{fmt(combined5YrAllIn)}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">incl 2 Dev Groups (Yrs 1–5)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OUTCOME TARGETS ============ */}
      <section className="bg-white py-20 lg:py-24 border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-8">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#8B0000]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">Section 5 · 5-Year Outcome Targets</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight text-[#0A192F]">
                What the model <em className="italic">delivers</em> in 5 years.
              </h2>
            </div>
            <div className="lg:col-span-7 text-sm text-[#334155] leading-relaxed lg:pt-3">
              <p>Same outcome architecture, scaled to each community&apos;s portfolio size. NH adds a Section 8 → homeownership transition track that Baytown doesn&apos;t have (Agency 1 in NH operates a 400-HH voucher portfolio explicitly). Both ladder up to <strong className="text-[#0A192F]">$25.45M</strong> in public benefits recovered and <strong className="text-[#0A192F]">1,656 evictions prevented</strong> across the regional cohort.</p>
            </div>
          </div>
          <div className="bg-[#F9F7F1] border border-[#CBD5E1] px-6 lg:px-8 pt-2 pb-5">
            <StatRow label="Cumulative Household-Years" a={(bSystem.householdsServed * 5).toLocaleString()} b={nhProgramGoalsTotals.householdYears.toLocaleString()} />
            <StatRow label="Workforce Enrollments" a={bGoals.leeCollegeEnrollmentTotal.toLocaleString()} b={nhProgramGoalsTotals.lonestarEnrollmentTotal.toLocaleString()} />
            <StatRow label="Workforce Graduates" a={bGoals.leeCollegeGraduatesTotal.toLocaleString()} b={nhProgramGoalsTotals.lonestarGraduatesTotal.toLocaleString()} />
            <StatRow label="Public Benefits Recovered" a={fmt(bGoals.benefitsRecoveredTotal)} b={fmt(nhProgramGoalsTotals.benefitsRecoveredTotal)} accent={RED} />
            <StatRow label="Evictions Prevented" a={bGoals.evictionsPreventedTotal.toLocaleString()} b={nhProgramGoalsTotals.evictionsPreventedTotal.toLocaleString()} />
            <StatRow label="Senior Home Modifications" a={bGoals.homeModsTotal.toLocaleString()} b={nhProgramGoalsTotals.homeModsTotal.toLocaleString()} />
            <StatRow label="Medicare / Extra Help Enrollments" a={bGoals.medicareEnrollmentsTotal.toLocaleString()} b={nhProgramGoalsTotals.medicareEnrollmentsTotal.toLocaleString()} />
            <StatRow label="Section 8 → Homeownership" a="N/A · no S8 cohort" b={nhProgramGoalsTotals.homeownershipTransitionsTotal.toLocaleString()} />
            <StatRow label="Seniors Aging-in-Place Stabilized" a="N/A · not tracked" b={nhProgramGoalsTotals.agingInPlaceStabilizedTotal.toLocaleString()} />
          </div>
        </div>
      </section>

      {/* ============ REGIONAL HUB CONSOLIDATION CALLOUT ============ */}
      <section className="bg-[#F9F7F1] py-20 lg:py-24 border-b border-[#CBD5E1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div data-testid="compare-regional-hub" className="bg-white border border-[#D4AF37] p-7 lg:p-10">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5">
                <GitMerge className="h-7 w-7 text-[#8B0000] mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">Section 6 · The Federal Pitch</div>
                <h2 className="font-display text-3xl lg:text-4xl leading-tight text-[#0A192F]">
                  Approve both — and the <em className="italic text-[#8B0000]">third year pays for itself.</em>
                </h2>
                <p className="mt-4 text-sm text-[#334155] leading-relaxed">
                  If Harris County funds both proposals concurrently and consolidates the two Central Hubs into a single Regional Coordination Unit at the Year-2 renewal, the County saves <strong className="text-[#0A192F]">{fmt(annualHubSavings)}/year</strong> in duplicated executive overhead alone — enough to fund a 4th Spoke or extend the model to a third community without raising the regional ask.
                </p>
                <p className="mt-3 text-sm text-[#334155] leading-relaxed">
                  This is the structural argument federal grant reviewers reward most: <em>&quot;Show me you&apos;ve designed for scale, not just for the first contract.&quot;</em>
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-2 gap-3">
                <div className="bg-[#0A192F] text-[#F9F7F1] p-5">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#D4AF37]">Today (2 Independent Hubs)</div>
                  <div className="font-display text-2xl text-[#F9F7F1] mt-2">{fmt(baselineHubCombined)}</div>
                  <div className="text-xs text-[#CBD5E1] mt-1">Combined Hub spend · 2 Executive Program Directors · 2 GMs · 2 Master LCSWs</div>
                </div>
                <div className="bg-[#8B0000] text-[#F9F7F1] p-5">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#D4AF37]">After Yr-2 Consolidation</div>
                  <div className="font-display text-2xl text-[#F9F7F1] mt-2">{fmt(regionalHubConsolidated)}</div>
                  <div className="text-xs text-[#F9F7F1]/85 mt-1">One Regional Hub · Modest staffing bump · Serves both communities</div>
                </div>
                <div className="bg-[#F9F7F1] border border-[#D4AF37] p-5 col-span-2">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B0000]">Annual Savings · Reinvestable</div>
                  <div className="font-display text-3xl text-[#0A192F] mt-2">{fmt(annualHubSavings)} <span className="text-base text-[#8B0000]">/ year</span></div>
                  <div className="text-xs text-[#475569] mt-2 leading-relaxed">
                    Could fund a 4th Spoke (~$916k–$1.14M / year), extend the model to a 3rd community (Pasadena, Aldine, La Porte), or backfill the District Operations budget at the Year-3 renewal.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTAs ============ */}
      <section className="bg-[#0A192F] text-[#F9F7F1] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">Open the full dossiers</div>
            <h2 className="font-display text-3xl lg:text-4xl leading-tight">
              Two proposals. One regional case for <em className="italic text-[#D4AF37]">federal funding.</em>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <Link
              to="/baytown"
              data-testid="compare-cta-baytown"
              className="bg-[#F9F7F1] hover:bg-white text-[#0A192F] p-7 group transition-colors flex flex-col"
            >
              <Building2 className="h-7 w-7 text-[#0A192F] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">Proposal 01</div>
              <div className="font-display text-2xl text-[#0A192F] leading-tight mb-2">The Baytown Bridge</div>
              <div className="text-xs text-[#475569] flex-1">1,500 HH · 3 × 500-HH Spokes · Lee College workforce track · ExxonMobil anchor employer base · ~$4.13M Yr-1.</div>
              <div className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#8B0000] group-hover:translate-x-1 transition-transform">
                Open the dossier <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
            <Link
              to="/north-houston"
              data-testid="compare-cta-nh"
              className="bg-[#D4AF37] hover:bg-[#E5C658] text-[#0A192F] p-7 group transition-colors flex flex-col"
            >
              <Building2 className="h-7 w-7 text-[#0A192F] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">Proposal 02</div>
              <div className="font-display text-2xl text-[#0A192F] leading-tight mb-2">North Houston District</div>
              <div className="text-xs text-[#0A192F]/80 flex-1">1,200 HH · 3 × 400-HH Spokes · LSNH / HCC North workforce track · IAH Corridor logistics employer mix · ~$3.46M Yr-1.</div>
              <div className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#8B0000] group-hover:translate-x-1 transition-transform">
                Open the dossier <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          </div>
          <p className="mt-10 max-w-3xl mx-auto text-center font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] leading-relaxed">
            Both proposals are illustrative documents created by Sterile Mate LLC for discussion with each community&apos;s leadership. Not yet adopted by Council, Board, or any funding agency. Document version: Proposal v1.0 · Subject to negotiation.
          </p>
        </div>
      </section>
      <SiteFooter />
    </main>
    </NDAGate>
  );
}
